// src/contexts/CurrencyContext.jsx
/**
 * Currency Context - Global Currency State Management
 * Provides currency selection, exchange rates, and conversion utilities
 * Follows the original multi-currency implementation plan
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// Currency configurations
const CURRENCIES = {
    GHS: { code: 'GHS', symbol: 'GH₵', name: 'Ghanaian Cedi', decimals: 2, country: 'GH', flagEmoji: '🇬🇭' },
    NGN: { code: 'NGN', symbol: '₦', name: 'Nigerian Naira', decimals: 2, country: 'NG', flagEmoji: '🇳🇬' },
    USD: { code: 'USD', symbol: '$', name: 'US Dollar', decimals: 2, country: 'US', flagEmoji: '🇺🇸' },
    KES: { code: 'KES', symbol: 'KSh', name: 'Kenyan Shilling', decimals: 2, country: 'KE', flagEmoji: '🇰🇪' },
    ZAR: { code: 'ZAR', symbol: 'R', name: 'South African Rand', decimals: 2, country: 'ZA', flagEmoji: '🇿🇦' },
    TZS: { code: 'TZS', symbol: 'TSh', name: 'Tanzanian Shilling', decimals: 2, country: 'TZ', flagEmoji: '🇹🇿' },
    UGX: { code: 'UGX', symbol: 'USh', name: 'Ugandan Shilling', decimals: 2, country: 'UG', flagEmoji: '🇺🇬' },
    RWF: { code: 'RWF', symbol: 'FRw', name: 'Rwandan Franc', decimals: 2, country: 'RW', flagEmoji: '🇷🇼' }
};

const COUNTRY_CURRENCY_MAP = {
    'GH': 'GHS',
    'NG': 'NGN',
    'US': 'USD',
    'KE': 'KES',
    'ZA': 'ZAR',
    'TZ': 'TZS',
    'UG': 'UGX',
    'RW': 'RWF',
    'GB': 'GBP',
    'EU': 'EUR',
    'CA': 'CAD',
    'AU': 'AUD',
    'CM': 'XAF',
    'SN': 'XOF',
    'CI': 'XOF',
    'ET': 'ETB',
    'EG': 'EGP',
    'MA': 'MAD',
    'ZM': 'ZMW',
    'MW': 'MWK'
};

const CurrencyContext = createContext();

export const CurrencyProvider = ({ children }) => {
    const [currency, setCurrencyState] = useState('GHS'); // Default currency
    const [rates, setRates] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(null);

    /**
     * Detect user's currency from locale/country
     */
    const detectCurrency = useCallback(async () => {
        // Try localStorage first
        const saved = localStorage.getItem('preferredCurrency');
        if (saved && CURRENCIES[saved]) {
            return saved;
        }

        // Try IP detection via backend proxy first
        try {
            const response = await api.get('/forex/detect-currency');
            if (response.data && response.data.success && response.data.currency) {
                return response.data.currency;
            }
        } catch (err) {
            console.warn('Backend IP currency detection failed, falling back to locale:', err);
        }

        // Try to detect from browser locale
        try {
            const locale = navigator.language || navigator.userLanguage;
            const countryCode = locale.split('-')[1]?.toUpperCase();

            if (countryCode && COUNTRY_CURRENCY_MAP[countryCode]) {
                return COUNTRY_CURRENCY_MAP[countryCode];
            }
        } catch (err) {
            console.warn('Could not detect currency from locale:', err);
        }

        // Default to GHS
        return 'GHS';
    }, []);

    /**
     * Fetch exchange rates from backend
     */
    const fetchRates = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await api.get('/forex/rates');

            if (response.data.success) {
                setRates(response.data.rates);
                setLastUpdate(new Date(response.data.timestamp));
                console.log('✅ Exchange rates loaded:', response.data.rates);
            } else {
                throw new Error('Failed to fetch rates');
            }
        } catch (err) {
            console.error('❌ Error fetching exchange rates:', err);
            setError(err.message);

            // Use fallback rates
            setRates({
                GHS: 12.21,
                NGN: 1400.0,
                KES: 129.5,
                ZAR: 18.5,
                TZS: 2350.0,
                UGX: 3700.0,
                RWF: 1100.0,
                USD: 1.0
            });
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Initialize on mount
     */
    useEffect(() => {
        const initCurrency = async () => {
            const detectedCurrency = await detectCurrency();
            setCurrencyState(detectedCurrency);
        };
        initCurrency();
        fetchRates();

        // Refresh rates every 30 minutes
        const interval = setInterval(fetchRates, 30 * 60 * 1000);

        return () => clearInterval(interval);
    }, [detectCurrency, fetchRates]);

    /**
     * Set user's preferred currency
     */
    const setCurrency = useCallback((newCurrency) => {
        if (!CURRENCIES[newCurrency]) {
            console.error('Invalid currency:', newCurrency);
            return;
        }

        setCurrencyState(newCurrency);
        localStorage.setItem('preferredCurrency', newCurrency);
        console.log(`💱 Currency changed to: ${newCurrency}`);
    }, []);

    /**
     * Convert amount between currencies
     */
    const convert = useCallback((amount, fromCurrency, toCurrency) => {
        if (fromCurrency === toCurrency) return amount;
        if (!rates[fromCurrency] || !rates[toCurrency]) {
            console.warn('Missing rates for conversion');
            return amount;
        }

        // Convert via USD: from -> USD -> to
        const spread = parseFloat(import.meta.env.VITE_CURRENCY_SPREAD ?? 9.25);
        const usdAmount = amount / rates[fromCurrency];
        const converted = usdAmount * rates[toCurrency] * (1 + spread / 100);

        return parseFloat(converted.toFixed(2));
    }, [rates]);

    /**
     * Format amount with currency symbol
     */
    const format = useCallback((amount, currencyCode = currency) => {
        if (!CURRENCIES[currencyCode]) {
            return `${amount}`;
        }

        const { symbol, decimals } = CURRENCIES[currencyCode];
        const formatted = parseFloat(amount).toFixed(decimals);

        // Add thousand separators
        const parts = formatted.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

        return `${symbol}${parts.join('.')}`;
    }, [currency]);

    /**
     * Get currency info
     */
    const getCurrencyInfo = useCallback((currencyCode = currency) => {
        return CURRENCIES[currencyCode] || CURRENCIES.GHS;
    }, [currency]);

    const value = {
        currency,
        setCurrency,
        currencies: CURRENCIES,
        supportedCurrencies: Object.values(CURRENCIES),
        rates,
        loading,
        error,
        lastUpdate,
        convert,
        format,
        getCurrencyInfo,
        refreshRates: fetchRates
    };

    return (
        <CurrencyContext.Provider value={value}>
            {children}
        </CurrencyContext.Provider>
    );
};

/**
 * Hook to use currency context
 */
export const useCurrency = () => {
    const context = useContext(CurrencyContext);

    if (!context) {
        throw new Error('useCurrency must be used within a CurrencyProvider');
    }

    return context;
};

export default CurrencyContext;
